import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Paper, Button, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert,
  Collapse, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Tabs, Tab, Switch, FormControlLabel,
} from "@mui/material";
import AddIcon        from "@mui/icons-material/Add";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalculateIcon  from "@mui/icons-material/Calculate";
import StorefrontIcon from "@mui/icons-material/Storefront";
import api from "../../utils/api";

/* ─────────────────────────────────────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────────────────────────────────────── */
const fmt    = (v)   => v != null ? `$${Number(v).toFixed(2)}` : "N/A";
const BTN_SX = {
  bgcolor: "#2f63ad", color: "#fff", fontWeight: 700,
  borderRadius: "10px", textTransform: "none", boxShadow: "none",
  "&:hover": { bgcolor: "#1f4f94", boxShadow: "none" },
};

function TierRangeLabel({ min, max }) {
  if (min == null && max == null)
    return <Chip label="All lengths" size="small" sx={{ fontSize: "0.72rem", height: 20 }} />;
  if (min == null) return <span>Under {max} ft</span>;
  if (max == null) return <span>{min} ft +</span>;
  return <span>{min}. {max} ft</span>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Add / Edit Tier dialog
───────────────────────────────────────────────────────────────────────────── */
const EMPTY = {
  label: "", min_vessel_length: "", max_vessel_length: "",
  daily_rate: "", monthly_rate: "", annual_rate: "", notes: "",
};

function TierDialog({ open, slip, tier, onClose, onSaved }) {
  const [form,   setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm(tier ? {
      label:             tier.label             ?? "",
      min_vessel_length: tier.min_vessel_length ?? "",
      max_vessel_length: tier.max_vessel_length ?? "",
      daily_rate:        tier.daily_rate        ?? "",
      monthly_rate:      tier.monthly_rate      ?? "",
      annual_rate:       tier.annual_rate       ?? "",
      notes:             tier.notes             ?? "",
    } : EMPTY);
  }, [open, tier]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.daily_rate && !form.monthly_rate && !form.annual_rate) {
      setErr("Enter at least one rate. daily, monthly, or annual.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const body = {
        label:             form.label             || null,
        min_vessel_length: form.min_vessel_length !== "" ? parseFloat(form.min_vessel_length) : null,
        max_vessel_length: form.max_vessel_length !== "" ? parseFloat(form.max_vessel_length) : null,
        daily_rate:        form.daily_rate        !== "" ? parseFloat(form.daily_rate)        : null,
        monthly_rate:      form.monthly_rate      !== "" ? parseFloat(form.monthly_rate)      : null,
        annual_rate:       form.annual_rate       !== "" ? parseFloat(form.annual_rate)       : null,
        notes:             form.notes             || null,
      };
      tier
        ? await api.put(`/marina-slips/${slip.id}/rates/${tier.id}`, body)
        : await api.post(`/marina-slips/${slip.id}/rates`, body);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to save. please try again.");
    } finally {
      setSaving(false);
    }
  };

  const FIELD = { size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px" } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
        {tier ? "Edit Rate Tier" : "Add Rate Tier"}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
          Slip {slip?.slip_number || slip?.slip_name}
          {slip?.max_length ? ` · max ${slip.max_length} ft` : ""}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: "12px !important" }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

        {/* Label */}
        <TextField fullWidth label="Tier label (optional)"
          placeholder='e.g. "Small vessels under 25 ft"'
          value={form.label} onChange={set("label")}
          {...FIELD} sx={{ ...FIELD.sx, mb: 2 }} />

        {/* Length range */}
        <Typography variant="caption"
          sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}>
          Vessel length range. leave blank for no limit on that end
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField label="Min length" type="number" fullWidth
            value={form.min_vessel_length} onChange={set("min_vessel_length")}
            inputProps={{ step: 0.5, min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            {...FIELD} />
          <TextField label="Max length" type="number" fullWidth
            value={form.max_vessel_length} onChange={set("max_vessel_length")}
            inputProps={{ step: 0.5, min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            {...FIELD} />
        </Box>

        {/* Rates */}
        <Typography variant="caption"
          sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}>
          Rates. at least one is required; omit any that don't apply
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          {[
            ["daily_rate",   "Daily"],
            ["monthly_rate", "Monthly"],
            ["annual_rate",  "Annual"],
          ].map(([key, label]) => (
            <TextField key={key} label={label} type="number" fullWidth
              value={form[key]} onChange={set(key)}
              inputProps={{ step: 0.01, min: 0 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              {...FIELD} />
          ))}
        </Box>

        {/* Notes */}
        <TextField fullWidth label="Notes (optional)" value={form.notes}
          onChange={set("notes")} multiline rows={2} {...FIELD} />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose}
          sx={{ borderRadius: "8px", textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={BTN_SX}>
          {tier ? "Save Changes" : "Add Tier"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Rate Calculator
───────────────────────────────────────────────────────────────────────────── */
function RateCalculator({ docks }) {
  const allSlips = useMemo(
    () => docks.flatMap(d => d.slips.map(s => ({ ...s, dock_name: d.dock_name }))),
    [docks]
  );

  const [slipId,       setSlipId]       = useState("");
  const [vesselLength, setVesselLength] = useState("");
  const [rateType,     setRateType]     = useState("monthly");
  const [result,       setResult]       = useState(null);
  const [busy,         setBusy]         = useState(false);

  const calculate = async () => {
    if (!slipId) return;
    setBusy(true);
    setResult(null);
    try {
      const params = { rate_type: rateType };
      if (vesselLength) params.vessel_length = vesselLength;
      const r = await api.get(`/marina-slips/${slipId}/rates/calculate`, { params });
      setResult(r.data);
    } catch (e) {
      setResult({ _err: e.response?.data?.message || "Calculation failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: "14px", p: 2, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <CalculateIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rate Calculator</Typography>
        <Typography variant="caption" color="text.secondary">
          Preview what rate applies for a given vessel length
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
        {/* Slip picker */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Slip</InputLabel>
          <Select value={slipId} label="Slip"
            onChange={e => { setSlipId(e.target.value); setResult(null); }}
            sx={{ borderRadius: "10px" }}>
            {docks.map(d => [
              <MenuItem key={`hdr-${d.dock_id}`} disabled
                sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", py: 0.25 }}>
                {d.dock_name}
              </MenuItem>,
              ...d.slips.map(s => (
                <MenuItem key={s.id} value={s.id} sx={{ pl: 3 }}>
                  {s.slip_number || s.slip_name}
                  {s.max_length ? ` (max ${s.max_length} ft)` : ""}
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>

        {/* Vessel length */}
        <TextField label="Vessel length" type="number" size="small"
          value={vesselLength}
          onChange={e => { setVesselLength(e.target.value); setResult(null); }}
          inputProps={{ step: 1, min: 0 }}
          InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
          sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />

        {/* Rate type */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Rate type</InputLabel>
          <Select value={rateType} label="Rate type"
            onChange={e => { setRateType(e.target.value); setResult(null); }}
            sx={{ borderRadius: "10px" }}>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </Select>
        </FormControl>

        {/* Button */}
        <Button variant="contained" size="small" onClick={calculate}
          disabled={!slipId || busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CalculateIcon />}
          sx={{ ...BTN_SX, height: 40 }}>
          Calculate
        </Button>

        {/* Result */}
        {result && (
          result._err
            ? <Alert severity="error" sx={{ py: 0.5, px: 1.5, borderRadius: "10px", fontSize: "0.8rem" }}>
                {result._err}
              </Alert>
            : <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                bgcolor:  result.source === "tier" ? "rgba(47,99,173,0.07)" : "rgba(0,0,0,0.03)",
                border:   `1px solid ${result.source === "tier" ? "rgba(47,99,173,0.25)" : "#e2e8f0"}`,
                borderRadius: "10px", px: 2, py: 0.75,
              }}>
                <Typography variant="h6"
                  sx={{ fontWeight: 800, color: result.source === "tier" ? "#2f63ad" : "#1e293b" }}>
                  {result.applied_rate != null ? `$${Number(result.applied_rate).toFixed(2)}` : "N/A"}
                  <Typography component="span" variant="caption"
                    sx={{ ml: 0.5, fontWeight: 400, color: "#64748b" }}>
                    / {rateType}
                  </Typography>
                </Typography>
                <Chip
                  label={result.source === "tier"
                    ? `Tier match${result.tier?.label ? `: ${result.tier.label}` : ""}`
                    : "Base rate"}
                  size="small"
                  color={result.source === "tier" ? "primary" : "default"}
                  sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
                />
                {vesselLength && (
                  <Typography variant="caption" color="text.secondary">
                    {vesselLength} ft vessel
                  </Typography>
                )}
                {result.source === "tier" && result.tier && (
                  <Typography variant="caption" color="text.secondary">
                    Range: <TierRangeLabel min={result.tier.min_vessel_length}
                                           max={result.tier.max_vessel_length} />
                  </Typography>
                )}
              </Box>
        )}
      </Box>
    </Paper>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Slip row. expandable tiers sub-table
───────────────────────────────────────────────────────────────────────────── */
function SlipRateRow({ slip, showSnack, onTierSaved }) {
  const [open,     setOpen]     = useState(false);
  const [tiers,    setTiers]    = useState(slip.tiers || []);
  const [loading,  setLoading]  = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [dialog,   setDialog]   = useState({ open: false, tier: null });

  const refreshTiers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/marina-slips/${slip.id}/rates`);
      setTiers(r.data.tiers || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [slip.id]);

  const handleDelete = async (tier) => {
    setDeleting(tier.id);
    try {
      await api.delete(`/marina-slips/${slip.id}/rates/${tier.id}`);
      setTiers(prev => prev.filter(t => t.id !== tier.id));
      onTierSaved();
      showSnack("Rate tier removed");
    } catch {
      showSnack("Failed to remove tier", "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleSaved = () => {
    refreshTiers();
    onTierSaved();
    showSnack(dialog.tier ? "Tier updated" : "Tier added");
  };

  const activeTiers = tiers.filter(t => t.is_active !== 0);

  return (
    <>
      {/* ── Slip summary row ── */}
      <TableRow hover sx={{ cursor: "pointer" }}
        onClick={() => setOpen(v => !v)}>
        <TableCell sx={{ fontWeight: 700, color: "#1e293b", py: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {open
              ? <ExpandLessIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
              : <ExpandMoreIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />}
            {slip.slip_number || slip.slip_name}
          </Box>
        </TableCell>
        <TableCell sx={{ color: "#64748b", fontSize: "0.82rem" }}>
          {slip.max_length ? `${slip.max_length} ft` : "N/A"}
          {slip.max_beam   ? ` × ${slip.max_beam} ft` : ""}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
          {fmt(slip.base_rates?.daily)}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
          {fmt(slip.base_rates?.monthly)}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
          {fmt(slip.base_rates?.annual)}
        </TableCell>
        <TableCell align="center">
          {activeTiers.length > 0
            ? <Chip
                label={`${activeTiers.length} tier${activeTiers.length !== 1 ? "s" : ""}`}
                size="small" color="primary"
                sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }} />
            : <Typography variant="caption" sx={{ color: "#cbd5e1" }}>base only</Typography>}
        </TableCell>
        <TableCell align="right" onClick={e => e.stopPropagation()}>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => { setOpen(true); setDialog({ open: true, tier: null }); }}
            sx={{
              borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.74rem",
              color: "#2f63ad", border: "1px solid rgba(47,99,173,0.3)",
              "&:hover": { bgcolor: "rgba(47,99,173,0.06)" },
            }}>
            Add Tier
          </Button>
        </TableCell>
      </TableRow>

      {/* ── Expanded tiers sub-table ── */}
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{
              bgcolor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              borderBottom: "1px solid #e2e8f0",
            }}>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!loading && activeTiers.length === 0 && (
                <Typography variant="body2" color="text.secondary"
                  sx={{ px: 5, py: 1.5, fontStyle: "italic" }}>
                  No size tiers. base rates apply to all vessels.
                  Use "Add Tier" to override rates by vessel length.
                </Typography>
              )}
              {!loading && activeTiers.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Label", "Length range", "Daily", "Monthly", "Annual", "Notes", ""].map((h, i) => (
                        <TableCell key={i}
                          align={i >= 2 && i <= 4 ? "right" : "left"}
                          sx={{
                            color: "#94a3b8", fontWeight: 700, fontSize: "0.7rem",
                            py: 0.75, pl: i === 0 ? 6 : undefined,
                            borderBottom: "1px solid #e2e8f0",
                          }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTiers.map(tier => (
                      <TableRow key={tier.id}
                        sx={{ "&:last-child td": { border: 0 },
                          "&:hover td": { bgcolor: "rgba(47,99,173,0.04)" } }}>
                        <TableCell sx={{ pl: 6, fontWeight: 600, fontSize: "0.82rem", py: 1 }}>
                          {tier.label || <span style={{ color: "#cbd5e1" }}>-</span>}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem" }}>
                          <TierRangeLabel min={tier.min_vessel_length} max={tier.max_vessel_length} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                          {fmt(tier.daily_rate)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                          {fmt(tier.monthly_rate)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                          {fmt(tier.annual_rate)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.78rem", color: "#64748b", maxWidth: 180 }}>
                          <Tooltip title={tier.notes || ""} placement="top">
                            <span>{tier.notes
                              ? tier.notes.length > 40 ? tier.notes.slice(0, 40) + "…" : tier.notes
                              : "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                            <Tooltip title="Edit tier">
                              <IconButton size="small"
                                onClick={() => setDialog({ open: true, tier })}>
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove tier">
                              <IconButton size="small" disabled={deleting === tier.id}
                                onClick={() => handleDelete(tier)}
                                sx={{ color: "#ef4444" }}>
                                {deleting === tier.id
                                  ? <CircularProgress size={13} sx={{ color: "#ef4444" }} />
                                  : <DeleteIcon sx={{ fontSize: 15 }} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <TierDialog
        open={dialog.open}
        slip={slip}
        tier={dialog.tier}
        onClose={() => setDialog({ open: false, tier: null })}
        onSaved={handleSaved}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Service Item Catalog
───────────────────────────────────────────────────────────────────────────── */
const SERVICE_CATEGORIES = [
  "fuel", "electric", "water", "pump_out", "storage",
  "amenity", "fee", "supply", "other",
];
const SERVICE_UNITS = ["gallon", "liter", "kwh", "day", "month", "each", "service", "hour"];

const BLANK_ITEM = {
  name: "", description: "", category: "fee", unit: "each",
  unit_price: "", tax_rate: "0", is_active: true,
};

function ServiceItemDialog({ open, marinaId, item, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK_ITEM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm(item ? {
      name: item.name ?? "",
      description: item.description ?? "",
      category: item.category ?? "fee",
      unit: item.unit ?? "each",
      unit_price: String(item.unit_price ?? ""),
      tax_rate: String(item.tax_rate ?? "0"),
      is_active: Boolean(item.is_active ?? 1),
    } : BLANK_ITEM);
  }, [open, item]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const FIELD = { size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px" } } };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr("Name is required."); return; }
    if (!form.unit_price) { setErr("Unit price is required."); return; }
    setSaving(true); setErr("");
    try {
      const payload = {
        marina_id: marinaId,
        name: form.name,
        description: form.description || null,
        category: form.category,
        unit: form.unit,
        unit_price: parseFloat(form.unit_price),
        tax_rate: parseFloat(form.tax_rate) || 0,
        is_active: form.is_active ? 1 : 0,
      };
      item
        ? await api.put(`/marina-service-items/${item.id}`, payload)
        : await api.post("/marina-service-items", payload);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to save item.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>{item ? "Edit Service Item" : "Add Service Item"}</DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

        <TextField fullWidth label="Name *" value={form.name} onChange={set("name")}
          {...FIELD} sx={{ ...FIELD.sx, mb: 2 }} />
        <TextField fullWidth label="Description" value={form.description} onChange={set("description")}
          multiline rows={2} {...FIELD} sx={{ ...FIELD.sx, mb: 2 }} />

        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <FormControl fullWidth {...FIELD}>
            <InputLabel>Category</InputLabel>
            <Select value={form.category} label="Category" onChange={set("category")} sx={{ borderRadius: "10px" }}>
              {SERVICE_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ textTransform: "capitalize" }}>{c.replace(/_/g, " ")}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth {...FIELD}>
            <InputLabel>Unit</InputLabel>
            <Select value={form.unit} label="Unit" onChange={set("unit")} sx={{ borderRadius: "10px" }}>
              {SERVICE_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField fullWidth label="Unit Price *" type="number" value={form.unit_price} onChange={set("unit_price")}
            inputProps={{ step: 0.01, min: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            {...FIELD} />
          <TextField fullWidth label="Tax Rate" type="number" value={form.tax_rate} onChange={set("tax_rate")}
            inputProps={{ step: 0.1, min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            {...FIELD} />
        </Box>

        {item && (
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} color="success" />}
            label="Active"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: "8px", textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={BTN_SX}>
          {item ? "Save Changes" : "Add Item"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const CATEGORY_COLORS = {
  fuel:     { bg: "rgba(234,88,12,0.1)",  color: "#ea580c" },
  electric: { bg: "rgba(234,179,8,0.12)", color: "#a16207" },
  water:    { bg: "rgba(6,182,212,0.1)",  color: "#0e7490" },
  pump_out: { bg: "rgba(139,92,246,0.1)", color: "#7c3aed" },
  storage:  { bg: "rgba(107,114,128,0.1)",color: "#374151" },
  amenity:  { bg: "rgba(16,185,129,0.1)", color: "#059669" },
  fee:      { bg: "rgba(47,99,173,0.1)",  color: "#2f63ad" },
  supply:   { bg: "rgba(245,158,11,0.1)", color: "#b45309" },
  other:    { bg: "rgba(107,114,128,0.08)", color: "#6b778c" },
};

function ServiceCatalogTab({ marinaId, showSnack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [deleting, setDeleting] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ marina_id: marinaId });
      if (catFilter) params.set("category", catFilter);
      const r = await api.get(`/marina-service-items?${params}`);
      setItems(r.data || []);
    } catch { showSnack("Failed to load service items", "error"); }
    finally { setLoading(false); }
  }, [marinaId, catFilter, showSnack]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    setDeleting(item.id);
    try {
      await api.delete(`/marina-service-items/${item.id}`);
      showSnack("Item deleted");
      fetchItems();
    } catch { showSnack("Failed to delete item", "error"); }
    finally { setDeleting(null); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {["", ...SERVICE_CATEGORIES].map((cat) => (
            <Chip
              key={cat || "all"}
              label={cat ? cat.replace(/_/g, " ") : "All"}
              onClick={() => setCatFilter(cat)}
              variant={catFilter === cat ? "filled" : "outlined"}
              sx={{
                textTransform: "capitalize", fontWeight: catFilter === cat ? 700 : 500, cursor: "pointer",
                ...(catFilter === cat ? { backgroundColor: "#2f63ad", color: "#fff" } : {}),
              }}
            />
          ))}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={BTN_SX}
          onClick={() => setDialog({ open: true, item: null })}>
          Add Item
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              {["Name", "Category", "Unit", "Price", "Tax", "Status", ""].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => (
                  <TableCell key={j}><Box sx={{ height: 16, backgroundColor: "#f0f0f0", borderRadius: 1 }} /></TableCell>
                ))}</TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                  <StorefrontIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                  <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                    {catFilter ? `No "${catFilter}" items yet.` : "No service items yet. Add your first item above."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : items.map((item) => {
              const style = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
              return (
                <TableRow key={item.id} hover sx={{ "&:last-child td": { border: 0 }, opacity: item.is_active ? 1 : 0.55 }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{item.name}</Typography>
                    {item.description && (
                      <Typography variant="caption" sx={{ color: "#9aa5b4" }}>
                        {item.description.length > 60 ? item.description.slice(0, 60) + "…" : item.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category?.replace(/_/g, " ")} size="small"
                      sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, textTransform: "capitalize",
                        backgroundColor: style.bg, color: style.color }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: "#6b778c" }}>{item.unit}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: "monospace", color: "#2f63ad" }}>
                    {fmt(item.unit_price)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>
                    {item.tax_rate ? `${item.tax_rate}%` : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.is_active ? "Active" : "Inactive"} size="small"
                      sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700,
                        backgroundColor: item.is_active ? "rgba(22,163,74,0.1)" : "rgba(107,114,128,0.1)",
                        color: item.is_active ? "#15803d" : "#6b778c" }} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setDialog({ open: true, item })}>
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton size="small" disabled={deleting === item.id} onClick={() => handleDelete(item)}
                      sx={{ color: "#ef4444" }}>
                      {deleting === item.id ? <CircularProgress size={13} sx={{ color: "#ef4444" }} /> : <DeleteIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ServiceItemDialog
        open={dialog.open}
        marinaId={marinaId}
        item={dialog.item}
        onClose={() => setDialog({ open: false, item: null })}
        onSaved={() => { fetchItems(); showSnack(dialog.item ? "Item updated" : "Item added"); }}
      />
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MarinaRates. main export
───────────────────────────────────────────────────────────────────────────── */
function RateTiersTab({ marinaId, showSnack }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [showCalc, setShowCalc] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marina-rates", { params: { marina_id: marinaId } });
      setData(r.data);
    } catch {
      showSnack("Failed to load rate data", "error");
    } finally {
      setLoading(false);
    }
  }, [marinaId, showSnack]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = useMemo(() => {
    if (!data) return null;
    const all        = data.docks.flatMap(d => d.slips);
    const withTiers  = all.filter(s => (s.tiers || []).length > 0).length;
    const monthlyArr = all.map(s => s.base_rates?.monthly).filter(Boolean);
    const avgMonthly = monthlyArr.length
      ? monthlyArr.reduce((a, b) => a + b, 0) / monthlyArr.length : null;
    return { total: all.length, withTiers, avgMonthly };
  }, [data]);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (!data || !data.docks?.length) return (
    <Alert severity="info" sx={{ borderRadius: "12px" }}>
      No slips found for this marina. Add slips in the Docks &amp; Slips tab first.
    </Alert>
  );

  return (
    <Box>
      {/* ── Summary cards + Calculator toggle ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3, alignItems: "center" }}>
        {[
          { label: "Total slips",      value: stats.total },
          { label: "Slips with tiers", value: stats.withTiers, chip: stats.withTiers > 0 },
          { label: "Avg base monthly", value: stats.avgMonthly != null ? `$${stats.avgMonthly.toFixed(2)}` : "N/A" },
        ].map(({ label, value, chip }) => (
          <Paper key={label} variant="outlined" sx={{ borderRadius: "14px", px: 2.5, py: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
              {chip && <Chip label="active" size="small" color="primary" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }} />}
            </Box>
          </Paper>
        ))}

        <Box sx={{ flex: 1 }} />

        <Button
          variant={showCalc ? "contained" : "outlined"}
          startIcon={<CalculateIcon />}
          onClick={() => setShowCalc(v => !v)}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, ...(showCalc ? BTN_SX : {}) }}>
          {showCalc ? "Hide Calculator" : "Rate Calculator"}
        </Button>
      </Box>

      {/* ── Rate Calculator ── */}
      <Collapse in={showCalc}>
        <RateCalculator docks={data.docks} />
      </Collapse>

      {/* ── Dock sections ── */}
      {data.docks.map(dock => (
        <Paper key={dock.dock_id} variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, bgcolor: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b" }}>{dock.dock_name}</Typography>
              <Chip label={`${dock.slips.length} slip${dock.slips.length !== 1 ? "s" : ""}`} size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {dock.slips.filter(s => (s.tiers || []).length > 0).length}/{dock.slips.length} slips have custom tiers
            </Typography>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                {[["Slip","left"],["Max size","left"],["Base daily","right"],["Base monthly","right"],["Base annual","right"],["Tiers","center"],["","right"]].map(([label, align]) => (
                  <TableCell key={label} align={align} sx={{ fontWeight: 700, color: "#64748b", fontSize: "0.75rem", py: 1 }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dock.slips.map(slip => (
                <SlipRateRow key={slip.id} slip={slip} showSnack={showSnack} onTierSaved={fetchData} />
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}
    </Box>
  );
}

export default function MarinaRates({ marinaId, showSnack }) {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: "1px solid #eef2f7", "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}>
        <Tab label="Rate Tiers" />
        <Tab label="Service Catalog" icon={<StorefrontIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
      </Tabs>
      {tab === 0 && <RateTiersTab marinaId={marinaId} showSnack={showSnack} />}
      {tab === 1 && <ServiceCatalogTab marinaId={marinaId} showSnack={showSnack} />}
    </Box>
  );
}
